import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { HOME_STATS } from '@/data/homeContent.js';

function Counter({ value, suffix, ordinal }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const startedAt = performance.now();
    const duration  = 1200;
    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setN(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  if (ordinal) {
    return (
      <span ref={ref} className="inline-flex items-start tabular-nums leading-none">
        <span className="text-gradient-brand">{n}</span>
        <sup className="text-[0.45em] font-semibold leading-none mt-[0.12em] -ml-[0.06em] text-brand-600 dark:text-brand-300">
          {ordinal}
        </sup>
      </span>
    );
  }

  return (
    <span ref={ref} className="tabular-nums text-gradient-brand">
      {n}{suffix}
    </span>
  );
}

export function StatsBand() {
  const bgRef = useRef(null);
  const bgInView = useInView(bgRef, { once: true, margin: '200px 0px' });

  return (
    <section ref={bgRef} className="relative py-16 lg:py-20 border-b border-ink-200/50 dark:border-ink-800/50 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.07] dark:opacity-[0.12] bg-cover bg-center transition-opacity duration-500"
        style={bgInView ? { backgroundImage: "url('/assets/images/venue/location/bangkok.webp')" } : undefined}
      />
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-b from-surface via-surface/95 to-surface dark:from-ink-950 dark:via-ink-950/95 dark:to-ink-950" />
      <div className="container">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {HOME_STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="text-center lg:text-left overflow-visible"
            >
              <p className="font-display text-5xl lg:text-6xl xl:text-7xl font-medium leading-tight overflow-visible pt-1">
                <Counter value={s.value} suffix={s.suffix} ordinal={s.ordinal} />
              </p>
              <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.18em] text-ink-500 dark:text-ink-400">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
